import {
  Bot,
  Clock,
  Crown,
  Database,
  Eye,
  File as FileIcon,
  FileMinus,
  FileText,
  FileUp,
  FolderOpen,
  Link,
  MessageSquare,
  MinusCircle,
  Pencil,
  PlusCircle,
  User,
  UserMinus,
  UserPlus,
} from "lucide-react";

// ---------------------------------------------------------------------------
// TabId type
// ---------------------------------------------------------------------------

export type TabId = "hub" | "resources" | "artifacts" | "members" | "settings";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Role / status / icon lookups
// ---------------------------------------------------------------------------

export function getRoleConfig(role: string) {
  switch (role) {
    case "owner":
      return { label: "Owner", color: "text-amber-400", bg: "bg-amber-500/20", icon: Crown };
    case "editor":
      return { label: "Editor", color: "text-blue-400", bg: "bg-blue-500/20", icon: Pencil };
    case "viewer":
      return { label: "Viewer", color: "text-slate-400", bg: "bg-slate-500/20", icon: Eye };
    default:
      return { label: role, color: "text-slate-400", bg: "bg-slate-500/20", icon: User };
  }
}

export function getStatusConfig(status: string) {
  switch (status) {
    case "active":
      return { label: "Active", bg: "bg-green-500/10", text: "text-green-400" };
    case "completed":
      return { label: "Completed", bg: "bg-blue-500/10", text: "text-blue-400" };
    case "archived":
      return { label: "Archived", bg: "bg-gray-500/10", text: "text-gray-400" };
    default:
      return { label: status, bg: "bg-gray-500/10", text: "text-gray-400" };
  }
}

export function getFileIcon(mimeType?: string) {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith("application/pdf")) return FileText;
  if (mimeType.includes("word") || mimeType.includes("document")) return FileText;
  if (mimeType.includes("sheet") || mimeType.includes("excel")) return FileText;
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return FileText;
  if (mimeType.startsWith("text/")) return FileText;
  return FileIcon;
}

export function getResourceIcon(type: string) {
  switch (type) {
    case "agent":
      return Bot;
    case "data_collection":
      return Database;
    case "chat_session":
      return MessageSquare;
    case "link":
      return Link;
    default:
      return Link;
  }
}

export function getActivityIcon(type: string) {
  switch (type) {
    case "project_created":
      return { icon: FolderOpen, color: "text-brand-primary", bg: "bg-brand-primary/10" };
    case "member_added":
      return { icon: UserPlus, color: "text-emerald-400", bg: "bg-emerald-500/10" };
    case "member_removed":
      return { icon: UserMinus, color: "text-red-400", bg: "bg-red-500/10" };
    case "resource_added":
      return { icon: PlusCircle, color: "text-blue-400", bg: "bg-blue-500/10" };
    case "resource_removed":
      return { icon: MinusCircle, color: "text-orange-400", bg: "bg-orange-500/10" };
    case "file_uploaded":
      return { icon: FileUp, color: "text-emerald-400", bg: "bg-emerald-500/10" };
    case "file_deleted":
      return { icon: FileMinus, color: "text-red-400", bg: "bg-red-500/10" };
    case "chat_started":
      return { icon: MessageSquare, color: "text-brand-primary", bg: "bg-brand-primary/10" };
    case "project_updated":
      return { icon: Pencil, color: "text-amber-400", bg: "bg-amber-500/10" };
    default:
      return { icon: Clock, color: "text-content-quaternary", bg: "bg-surface-secondary" };
  }
}

export function getFileStatusConfig(status: string): { color: string; label: string } {
  switch (status) {
    case "ready":
      return { color: "text-emerald-400", label: "Indexed" };
    case "pending":
      return { color: "text-amber-400", label: "Pending" };
    case "processing":
      return { color: "text-amber-400", label: "Processing" };
    case "failed":
      return { color: "text-red-400", label: "Failed" };
    default:
      return { color: "text-content-quaternary", label: status };
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const PROJECT_ROLE_OPTIONS = [
  { value: "owner" as const, label: "Owner", icon: Crown, description: "Full project management access" },
  { value: "editor" as const, label: "Editor", icon: Pencil, description: "Can edit resources and content" },
  { value: "viewer" as const, label: "Viewer", icon: Eye, description: "Read-only access" },
];
